// src/pages/message-center/components/ShiftLeaderCommissionModal.jsx
// Reuses ShiftTypeModal.module.css directly — same overlay/header/body/footer/slab-editor
// visual structure, just one tier editor per phase instead of one slab list per shift type.
import React, { useState, useEffect } from 'react';
import { X, Check, Loader, Plus, Trash2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import FieldError from './FieldError';
import { validateRanges, blockInvalidNumberKeys, blockNonIntegerKeys, sanitizeNumber } from '../utils/rangeValidation';
import styles from './ShiftTypeModal.module.css';

const EMPTY_TIER = { minValue: '', maxValue: '', rate: '', fixedAmount: '' };
const EMPTY_PHASE = () => ({ targetValue: '', tiers: [{ ...EMPTY_TIER }] });

const EMPTY_FORM = {
  enabled: false,
  calculationMethod: 'per_sales_value',
  calculationBase: 'percentage',
  phaseCount: 1,
  phases: [EMPTY_PHASE()],
};

const BASE_OPTIONS = [
  { value: 'percentage', label: 'Percentage' },
  { value: 'fixed', label: 'Fixed Amount' },
];

const isTierEmpty = (tier) => Object.values(tier).every((v) => v === '');

const ShiftLeaderCommissionModal = ({ onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { settings, isLoading, saveSettings } = useBusinessSettings('messageCenterShiftLeaderCommission');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  const valueField = formData.calculationBase === 'percentage' ? 'rate' : 'fixedAmount';

  useEffect(() => {
    if (isLoading) return;
    if (settings && settings.enabled !== undefined) {
      setFormData({
        enabled: !!settings.enabled,
        calculationMethod: settings.calculationMethod || 'per_sales_value',
        calculationBase: settings.calculationBase || 'percentage',
        phaseCount: settings.phaseCount || 1,
        phases: Array.isArray(settings.phases) && settings.phases.length > 0
          ? settings.phases.map((phase) => ({
              targetValue: phase.targetValue != null ? String(phase.targetValue) : '',
              tiers: Array.isArray(phase.tiers) && phase.tiers.length > 0
                ? phase.tiers.map((tier) => ({
                    minValue: tier.minValue != null ? String(tier.minValue) : '',
                    maxValue: tier.maxValue != null ? String(tier.maxValue) : '',
                    rate: tier.rate != null ? String(tier.rate) : '',
                    fixedAmount: tier.fixedAmount != null ? String(tier.fixedAmount) : '',
                  }))
                : [{ ...EMPTY_TIER }],
            }))
          : [EMPTY_PHASE()],
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, settings]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'calculationBase') setErrors({});
  };

  const handlePhaseCountChange = (value) => {
    const count = Math.max(1, parseInt(sanitizeNumber(value, false), 10) || 1);
    setFormData((prev) => {
      const phases = [...prev.phases];
      while (phases.length < count) phases.push(EMPTY_PHASE());
      phases.length = count;
      return { ...prev, phaseCount: count, phases };
    });
    setErrors({});
  };

  const handlePhaseFieldChange = (phaseIndex, field, value) => {
    setFormData((prev) => {
      const phases = [...prev.phases];
      phases[phaseIndex] = { ...phases[phaseIndex], [field]: sanitizeNumber(value) };
      return { ...prev, phases };
    });
    const errorKey = `phases.${phaseIndex}.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '' }));
    }
  };

  const handleAddTier = (phaseIndex) => {
    setFormData((prev) => {
      const phases = [...prev.phases];
      const tiers = phases[phaseIndex].tiers;
      const last = tiers[tiers.length - 1];
      const nextMin = last && /^\d+$/.test(last.maxValue) ? String(parseInt(last.maxValue, 10) + 1) : '';
      phases[phaseIndex] = { ...phases[phaseIndex], tiers: [...tiers, { ...EMPTY_TIER, minValue: nextMin }] };
      return { ...prev, phases };
    });
    setErrors({});
  };

  const handleTierChange = (phaseIndex, tierIndex, field, value) => {
    const clean = sanitizeNumber(value, field === 'rate' || field === 'fixedAmount');
    setFormData((prev) => {
      const phases = [...prev.phases];
      const tiers = [...phases[phaseIndex].tiers];
      tiers[tierIndex] = { ...tiers[tierIndex], [field]: clean };
      phases[phaseIndex] = { ...phases[phaseIndex], tiers };
      return { ...prev, phases };
    });
    const errorKey = `phases.${phaseIndex}.tiers.${tierIndex}.${field}`;
    const rowKey = `phases.${phaseIndex}.tiers.${tierIndex}.row`;
    if (errors[errorKey] || errors[rowKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', [rowKey]: '' }));
    }
  };

  const handleRemoveTier = (phaseIndex, tierIndex) => {
    setFormData((prev) => {
      const phases = [...prev.phases];
      phases[phaseIndex] = { ...phases[phaseIndex], tiers: phases[phaseIndex].tiers.filter((_, i) => i !== tierIndex) };
      return { ...prev, phases };
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    formData.phases.forEach((phase, phaseIndex) => {
      if (phase.targetValue !== '' && (isNaN(phase.targetValue) || parseFloat(phase.targetValue) < 0)) {
        newErrors[`phases.${phaseIndex}.targetValue`] = t('messageCenterShiftLeaderCommission.validation.targetNegative');
      }
      Object.assign(newErrors, validateRanges(phase.tiers, { valueField, prefix: `phases.${phaseIndex}.tiers`, t }));
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Any entered tiers are always validated; a fully empty config may be saved while disabled.
    const hasAnyInput = formData.phases.some((p) => p.targetValue !== '' || p.tiers.some((tier) => !isTierEmpty(tier)));
    if ((formData.enabled || hasAnyInput) && !validate()) {
      showError(t('messageCenterShiftTypes.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        enabled: formData.enabled,
        calculationMethod: formData.calculationMethod,
        calculationBase: formData.calculationBase,
        phaseCount: formData.phaseCount,
        phases: formData.phases.map((phase) => ({
          targetValue: phase.targetValue === '' ? null : parseFloat(phase.targetValue),
          tiers: phase.tiers.map((tier) => ({
            minValue: parseFloat(tier.minValue),
            maxValue: tier.maxValue === '' ? null : parseFloat(tier.maxValue),
            ...(formData.calculationBase === 'percentage'
              ? { rate: parseFloat(tier.rate) }
              : { fixedAmount: parseFloat(tier.fixedAmount) }),
          })),
        })),
      };

      await saveSettings(payload);
      success(t('messageCenterShiftLeaderCommission.saveSuccess'));
      onSuccess();
    } catch (err) {
      console.error('Error saving shift leader commission settings:', err);
      showError(err.response?.data?.message || t('messageCenterShiftLeaderCommission.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const minPlaceholder = (tiers, index) => {
    if (index === 0) return t('messageCenterShiftTypes.placeholders.firstMin');
    const prevMax = tiers[index - 1]?.maxValue;
    return /^\d+$/.test(prevMax)
      ? t('messageCenterShiftTypes.placeholders.nextMin', { value: parseInt(prevMax, 10) + 1 })
      : '';
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{t('messageCenterShiftLeaderCommission.modalTitle')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <label>{t('common.status')}</label>
              <div className={`${styles.toggleRow} ${styles.toggleRowLarge}`}>
                <div className={styles.toggleText}>
                  <span className={styles.toggleTitle}>{t('messageCenterShiftLeaderCommission.enabled')}</span>
                  <span className={styles.toggleHelp}>{t('messageCenterShiftLeaderCommission.enabledHelp')}</span>
                </div>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>

            <div className={styles.tripleRow}>
            <div className={styles.formGroup}>
              <label>{t('messageCenterShiftLeaderCommission.calculationMethod')}</label>
              <div className={styles.lockedMethod}>{t('messageCenterShiftLeaderCommission.perSalesValue')}</div>
              <p className={styles.helpText}>{t('messageCenterShiftLeaderCommission.otherMethodsComingSoon')}</p>
            </div>

            <div className={styles.formGroup}>
              <label>{t('messageCenterShiftLeaderCommission.calculationBase')}</label>
              <Dropdown
                value={formData.calculationBase}
                onChange={(v) => handleChange('calculationBase', v)}
                options={BASE_OPTIONS}
                displayKey="label"
                valueKey="value"
                allowClear={false}
                disabled={isSubmitting}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phaseCount">{t('messageCenterShiftLeaderCommission.phaseCount')}</label>
              <input
                type="number"
                id="phaseCount"
                min="1"
                step="1"
                className={styles.plainInput}
                placeholder={t('messageCenterShiftLeaderCommission.phaseCountPlaceholder')}
                value={formData.phaseCount}
                onKeyDown={blockNonIntegerKeys}
                onChange={(e) => handlePhaseCountChange(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            </div>

            {formData.phases.map((phase, phaseIndex) => (
              <div className={`${styles.formGroup} ${styles.fullWidth}`} key={phaseIndex}>
                <div className={styles.phaseHeader}>
                  <label>{t('messageCenterShiftLeaderCommission.phase')} {phaseIndex + 1}</label>
                  <div className={`${styles.phaseTarget} ${styles.fieldCell}`}>
                    <span>{t('messageCenterShiftLeaderCommission.targetValue')}</span>
                    <input
                      type="number"
                      id={`phaseTarget-${phaseIndex}`}
                      min="0"
                      step="0.01"
                      className={`${styles.plainInput} ${errors[`phases.${phaseIndex}.targetValue`] ? styles.error : ''}`}
                      placeholder={t('messageCenterShiftLeaderCommission.targetValuePlaceholder')}
                      value={phase.targetValue}
                      onKeyDown={blockInvalidNumberKeys}
                      onChange={(e) => handlePhaseFieldChange(phaseIndex, 'targetValue', e.target.value)}
                      disabled={isSubmitting}
                    />
                    <FieldError message={errors[`phases.${phaseIndex}.targetValue`]} floating />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddTier(phaseIndex)}
                    className={styles.addSlabButton}
                    disabled={isSubmitting}
                  >
                    <Plus size={16} />
                    {t('messageCenterShiftLeaderCommission.addTier')}
                  </button>
                </div>

                <div className={styles.slabsList}>
                  <div className={styles.slabsListHeader}>
                    <span>{t('messageCenterShiftTypes.minValue')}</span>
                    <span>{t('messageCenterShiftTypes.maxValue')}</span>
                    <span>
                      {formData.calculationBase === 'percentage'
                        ? t('messageCenterShiftTypes.rate')
                        : t('messageCenterShiftLeaderCommission.fixedAmount')}
                    </span>
                    <span />
                  </div>
                  {phase.tiers.map((tier, tierIndex) => {
                    const isLast = tierIndex === phase.tiers.length - 1;
                    const err = (field) => errors[`phases.${phaseIndex}.tiers.${tierIndex}.${field}`];
                    return (
                      <div className={`${styles.slabRow} ${err('row') ? styles.rowError : ''}`} key={tierIndex}>
                        <div className={styles.fieldCell}>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder={minPlaceholder(phase.tiers, tierIndex)}
                            value={tier.minValue}
                            onKeyDown={blockNonIntegerKeys}
                            onChange={(e) => handleTierChange(phaseIndex, tierIndex, 'minValue', e.target.value)}
                            className={err('minValue') ? styles.error : ''}
                            disabled={isSubmitting}
                          />
                          <FieldError message={err('minValue')} floating />
                        </div>
                        <div className={styles.fieldCell}>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            placeholder={isLast ? t('messageCenterShiftTypes.openEnded') : t('messageCenterShiftTypes.placeholders.maxValue')}
                            value={tier.maxValue}
                            onKeyDown={blockNonIntegerKeys}
                            onChange={(e) => handleTierChange(phaseIndex, tierIndex, 'maxValue', e.target.value)}
                            className={err('maxValue') ? styles.error : ''}
                            disabled={isSubmitting}
                          />
                          <FieldError message={err('maxValue')} floating />
                        </div>
                        <div className={styles.fieldCell}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={t(`messageCenterShiftTypes.placeholders.${valueField}`)}
                            value={tier[valueField]}
                            onKeyDown={blockInvalidNumberKeys}
                            onChange={(e) => handleTierChange(phaseIndex, tierIndex, valueField, e.target.value)}
                            className={err(valueField) ? styles.error : ''}
                            disabled={isSubmitting}
                          />
                          <FieldError message={err(valueField)} floating />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveTier(phaseIndex, tierIndex)}
                          className={styles.removeSlabButton}
                          disabled={isSubmitting || phase.tiers.length === 1}
                          title={t('messageCenterShiftTypes.removeSlab')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </form>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Check size={18} />
                {t('common.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftLeaderCommissionModal;
